// File: src/components/pages/tracker/BuildTracker.jsx
// 3-step wizard: Select → Verify → Publish
// "Save & Continue" validates and saves before advancing.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CheckCircle2, PlusCircle, Trash2, X,
  ChevronDown, AlertCircle, LayoutTemplate, ChevronRight, Clock, Send, Sparkles
} from 'lucide-react';
import { trackerTemplateAPI, trackerLevelsTemplateAPI, trackerLevelsAPI, instructorConfigAPI, trackerDefaultTemplateAPI } from '../../../services/api';
import '../../../styles/unified.css';

const STEPS = ['Select', 'Verify', 'Publish'];

const uid = () => `lvl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// ─────────────────────────────────────────────────────────────────────────────
const BuildTracker = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [step, setStep] = useState(0);

  // ── Max tracker levels — loaded from InstructorConfiguration ───────────────
  const [maxTrackerLevels, setMaxTrackerLevels] = useState(5);
  const levelOptions = Array.from({ length: maxTrackerLevels }, (_, i) => i + 1);

  // ── Step 0 — Select ────────────────────────────────────────────────────────
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates,   setLoadingTemplates]   = useState(false);
  const [levels, setLevels] = useState([
    { uid: uid(), levelNumber: 1, selectedTemplates: [], showPicker: false, showDefaultPicker: false }
  ]);
  const [existingConfigId, setExistingConfigId] = useState(null);
  const [loadingExisting,  setLoadingExisting]  = useState(true);
  const [showEditBanner,   setShowEditBanner]   = useState(false);
  const [isSaving,  setIsSaving]  = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Default templates ──────────────────────────────────────────────────────
  const [defaultTemplates,      setDefaultTemplates]      = useState([]);
  const [loadingDefaults,       setLoadingDefaults]       = useState(false);
  const [creatingFromDefault,   setCreatingFromDefault]   = useState(null); // levelUid being acted on

  // ── Step 1 — Verify ────────────────────────────────────────────────────────
  const [verifyData,    setVerifyData]    = useState([]);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [verifyError,   setVerifyError]   = useState(null);

  // ── Step 2 — Publish ───────────────────────────────────────────────────────
  const [publishLevels,     setPublishLevels]     = useState([]);
  const [publishedRecord,   setPublishedRecord]   = useState(null);  // existing TrackerLevels entry
  const [publishConfigDate, setPublishConfigDate] = useState(null);

  // ── Diff check: is the draft different from what's published? ───────────────
  // Compares level numbers and their assigned templateIds (order-independent).
  // True when no published record exists yet, or when any level/template differs.
  const hasDiff = useMemo(() => {
    if (!publishedRecord) return true;

    const normalise = (levels) =>
      (levels || [])
        .map(lv => ({
          level:     Number(lv.level),
          templates: (lv.templates || []).map(t => t.templateId).sort()
        }))
        .sort((a, b) => a.level - b.level);

    return JSON.stringify(normalise(publishLevels)) !==
           JSON.stringify(normalise(publishedRecord.levels));
  }, [publishLevels, publishedRecord]);
  const [loadingPublish,    setLoadingPublish]    = useState(false);
  const [publishLoadError,  setPublishLoadError]  = useState(null);
  const [isPublishing,      setIsPublishing]      = useState(false);
  const [publishError,      setPublishError]      = useState(null);
  const [publishSuccess,    setPublishSuccess]    = useState(false);

  // Close any open picker when clicking outside
  const containerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setLevels(prev => prev.map(l => ({ ...l, showPicker: false })));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load MaxTrackerLevels from InstructorConfiguration ─────────────────────
  const loadMaxTrackerLevels = useCallback(async () => {
    try {
      const res = await instructorConfigAPI.getConfiguration(getAccessTokenSilently);
      if (res.success && res.data?.maxTrackerLevels) {
        setMaxTrackerLevels(Number(res.data.maxTrackerLevels));
      }
    } catch (err) {
      console.error('Error loading instructor config:', err);
      // Keep default of 5 on failure
    }
  }, [getAccessTokenSilently]);

  // ── Load available TrackerTemplates ─────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await trackerTemplateAPI.getTemplates(getAccessTokenSilently);
      if (res.success) setAvailableTemplates(res.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [getAccessTokenSilently]);

  // ── Load existing TrackerLevelsTemplate to enable editing ──────────────────
  const loadExistingConfig = useCallback(async () => {
    setLoadingExisting(true);
    try {
      const res = await trackerLevelsTemplateAPI.getConfigs(getAccessTokenSilently);
      if (res.success && res.data && res.data.length > 0) {
        const latest = res.data[0]; // newest-first from API
        setExistingConfigId(latest.configId);
        setShowEditBanner(true);
        setTimeout(() => setShowEditBanner(false), 4000);
        setLevels(
          (latest.levels || []).map(lv => ({
            uid:               uid(),
            levelNumber:       lv.level,
            selectedTemplates: lv.templates || [],
            showPicker:        false,
            showDefaultPicker: false
          }))
        );
      }
    } catch (err) {
      console.error('Error loading existing config:', err);
    } finally {
      setLoadingExisting(false);
    }
  }, [getAccessTokenSilently]);

  // ── Load default templates from TrackerDefaultTemplates table ───────────────
  const loadDefaultTemplates = useCallback(async () => {
    setLoadingDefaults(true);
    try {
      const res = await trackerDefaultTemplateAPI.getAll(getAccessTokenSilently);
      if (res.success) setDefaultTemplates(res.data || []);
    } catch (err) {
      console.error('Error loading default templates:', err);
    } finally {
      setLoadingDefaults(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadMaxTrackerLevels();
    loadTemplates();
    loadExistingConfig();
    loadDefaultTemplates();
  }, [loadMaxTrackerLevels, loadTemplates, loadExistingConfig, loadDefaultTemplates]);

  // ── Load Verify data ────────────────────────────────────────────────────────
  const loadVerifyData = useCallback(async () => {
    setLoadingVerify(true);
    setVerifyError(null);
    try {
      const res = await trackerLevelsTemplateAPI.getConfigs(getAccessTokenSilently);
      if (res.success) setVerifyData(res.data || []);
      else setVerifyError(res.message || 'Failed to load configurations');
    } catch (err) {
      setVerifyError(err.message || 'Failed to load configurations');
    } finally {
      setLoadingVerify(false);
    }
  }, [getAccessTokenSilently]);

  // ── Load Publish data ────────────────────────────────────────────────────────
  // Fetches TrackerLevelsTemplate (draft, left column) and TrackerLevels
  // (existing published record, right column) in parallel.
  const loadPublishData = useCallback(async () => {
    setLoadingPublish(true);
    setPublishLoadError(null);
    setPublishSuccess(false);
    setPublishError(null);
    try {
      const [templatesRes, configsRes, publishedRes] = await Promise.all([
        trackerTemplateAPI.getTemplates(getAccessTokenSilently),
        trackerLevelsTemplateAPI.getConfigs(getAccessTokenSilently),
        trackerLevelsAPI.getAll(getAccessTokenSilently)
      ]);

      // Build templateId → full template lookup for item expansion
      const templateMap = {};
      (templatesRes.data || []).forEach(t => { templateMap[t.templateId] = t; });

      // ── Draft levels (left column) ──────────────────────────────────────────
      if (configsRes.success && configsRes.data && configsRes.data.length > 0) {
        const latest = configsRes.data[0];
        setPublishConfigDate(latest.createdAt);
        const expanded = (latest.levels || []).map(lv => ({
          level:     lv.level,
          templates: (lv.templates || []).map(selTpl => {
            const full = templateMap[selTpl.templateId];
            return {
              templateId: selTpl.templateId,
              heading:    selTpl.heading,
              items:      full?.items || []
            };
          })
        }));
        setPublishLevels(expanded);
      } else {
        setPublishLevels([]);
      }

      // ── Published record (right column) ────────────────────────────────────
      if (publishedRes.success && publishedRes.data && publishedRes.data.length > 0) {
        setPublishedRecord(publishedRes.data[0]); // most recent
      } else {
        setPublishedRecord(null);
      }
    } catch (err) {
      setPublishLoadError(err.message || 'Failed to load configuration.');
    } finally {
      setLoadingPublish(false);
    }
  }, [getAccessTokenSilently]);

  // ── Publish / Update TrackerLevels ──────────────────────────────────────────
  // Updates the existing record if one exists, creates a new one otherwise.
  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishError(null);
    try {
      const payload = { sourceConfigId: existingConfigId, levels: publishLevels };

      let res;
      if (publishedRecord?.trackerLevelId) {
        res = await trackerLevelsAPI.updateRecord(
          publishedRecord.trackerLevelId, payload, getAccessTokenSilently
        );
      } else {
        res = await trackerLevelsAPI.publish(payload, getAccessTokenSilently);
      }

      if (res.success) {
        setPublishSuccess(true);
        setTimeout(() => setPublishSuccess(false), 4000);
        // Refresh right column with the newly saved data
        const refreshed = await trackerLevelsAPI.getAll(getAccessTokenSilently);
        if (refreshed.success && refreshed.data && refreshed.data.length > 0) {
          setPublishedRecord(refreshed.data[0]);
        }
      } else {
        setPublishError(res.message || 'Failed to publish configuration.');
      }
    } catch (err) {
      setPublishError(err.message || 'Failed to publish configuration.');
    } finally {
      setIsPublishing(false);
    }
  };

  const formatDate = (d) => !d ? '' : new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  // ── Navigation with side-effects ────────────────────────────────────────────
  const goForward = (targetStep) => {
    setStep(targetStep);
    if (targetStep === 1) loadVerifyData();
    if (targetStep === 2) loadPublishData();
  };

  // ── Level helpers ───────────────────────────────────────────────────────────
  const addLevel = () =>
    setLevels(prev => [
      ...prev,
      { uid: uid(), levelNumber: 1, selectedTemplates: [], showPicker: false, showDefaultPicker: false }
    ]);

  const removeLevel = (id) =>
    setLevels(prev => prev.filter(l => l.uid !== id));

  const setLevelNumber = (id, n) =>
    setLevels(prev => prev.map(l => l.uid === id ? { ...l, levelNumber: n } : l));

  const togglePicker = (id) =>
    setLevels(prev => prev.map(l =>
      l.uid === id
        ? { ...l, showPicker: !l.showPicker }
        : { ...l, showPicker: false }
    ));

  const toggleDefaultPicker = (id) =>
    setLevels(prev => prev.map(l =>
      l.uid === id
        ? { ...l, showDefaultPicker: !l.showDefaultPicker }
        : { ...l, showDefaultPicker: false }
    ));

  // handleAddFromDefault: create an instructor-owned copy of the default template,
  // then add it to the level just like a regular template.
  const handleAddFromDefault = async (levelUid, defaultTpl) => {
    // Close picker immediately for snappy UX
    setLevels(prev => prev.map(l =>
      l.uid === levelUid ? { ...l, showDefaultPicker: false } : l
    ));
    setCreatingFromDefault(levelUid);

    try {
      // Check if instructor already has a template with the same heading
      const existing = availableTemplates.find(
        t => t.heading.trim().toLowerCase() === defaultTpl.heading.trim().toLowerCase()
      );

      let tplToAdd;
      if (existing) {
        // Re-use existing instructor template — avoid duplicates
        tplToAdd = existing;
      } else {
        // Create a new instructor-owned copy
        const res = await trackerTemplateAPI.createTemplate(
          { heading: defaultTpl.heading, items: defaultTpl.items },
          getAccessTokenSilently
        );
        if (!res.success) throw new Error(res.message || 'Failed to create template from default');
        tplToAdd = res.data;
        // Refresh template list so the new entry appears in "Add Template" too
        await loadTemplates();
      }

      // Add to level with fromDefault flag for visual differentiation
      addTemplate(levelUid, { ...tplToAdd, fromDefault: true });
    } catch (err) {
      console.error('Error adding from default template:', err);
      setSaveError(err.message || 'Failed to add default template.');
    } finally {
      setCreatingFromDefault(null);
    }
  };

  const addTemplate = (id, tpl) =>
    setLevels(prev => prev.map(l => {
      if (l.uid !== id) return l;
      if (l.selectedTemplates.some(t => t.templateId === tpl.templateId)) return l;
      return { ...l, selectedTemplates: [...l.selectedTemplates, tpl], showPicker: false };
    }));

  const removeTemplate = (levelId, templateId) =>
    setLevels(prev => prev.map(l =>
      l.uid === levelId
        ? { ...l, selectedTemplates: l.selectedTemplates.filter(t => t.templateId !== templateId) }
        : l
    ));

  const availableFor = (level) => {
    const used = new Set(level.selectedTemplates.map(t => t.templateId));
    return availableTemplates.filter(t => !used.has(t.templateId));
  };

  const isValid = levels.length > 0 && levels.every(l => l.selectedTemplates.length > 0);

  // ── Save & Continue ─────────────────────────────────────────────────────────
  const handleSaveAndContinue = async () => {
    if (!isValid) {
      setSaveError('Each level must have at least one template selected.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = {
        levels: levels.map(l => ({
          level:     l.levelNumber,
          templates: l.selectedTemplates.map(t => ({
            templateId: t.templateId,
            heading:    t.heading
          }))
        }))
      };

      let res;
      if (existingConfigId) {
        res = await trackerLevelsTemplateAPI.updateConfig(
          existingConfigId, payload, getAccessTokenSilently
        );
      } else {
        res = await trackerLevelsTemplateAPI.createConfig(payload, getAccessTokenSilently);
        if (res.success && res.data?.configId) {
          setExistingConfigId(res.data.configId);
        }
      }

      if (res.success) {
        setShowEditBanner(false);
        goForward(1);
      } else {
        setSaveError(res.message || 'Failed to save configuration');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef}>
      {/* Step indicator */}
      <div className="tracker-stepper">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`tracker-step ${i < step ? 'step-done' : ''} ${i === step ? 'step-active' : ''}`}>
              <div className="step-circle">
                {i < step ? <CheckCircle2 size={20} /> : <span>{i + 1}</span>}
              </div>
              <span className="step-label">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`step-connector ${i < step ? 'connector-done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 0 — Select ─────────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="tracker-step-panel">
          <h3 className="step-panel-title">Configure Levels</h3>
          <p className="step-panel-subtitle">
            Assign one or more templates to each level, then click <strong>Save &amp; Continue</strong>.
          </p>

          {showEditBanner && (
            <div className="success-banner" style={{ marginBottom: '1rem' }}>
              <CheckCircle2 size={16} />
              <span>Editing existing configuration — <strong>Save &amp; Continue</strong> will update it.</span>
            </div>
          )}

          {saveError && (
            <div className="error-banner" style={{ marginBottom: '1rem' }}>
              <AlertCircle size={16} /><span>{saveError}</span>
            </div>
          )}

          {loadingTemplates || loadingExisting ? (
            <div className="loading-state"><div className="spinner" /><p>Loading…</p></div>
          ) : availableTemplates.length === 0 ? (
            <div className="empty-state">
              <LayoutTemplate size={40} style={{ color: '#94a3b8' }} />
              <p>No templates found. Create templates first in the Templates tab.</p>
              <p><strong>IMP: </strong> Create a sample template first in the Templates tab before it appears here for selection.</p>
            </div>
          ) : (
            <div className="bt-levels-list">
              {levels.map((level) => {
                const pickable = availableFor(level);
                return (
                  <div key={level.uid} className="bt-level-card">
                    <div className="bt-level-card-header">
                      <span className="bt-level-card-index">Level</span>
                      <div className="bt-level-select-wrap">
                        <select
                          className="bt-level-select"
                          value={level.levelNumber}
                          onChange={e => setLevelNumber(level.uid, Number(e.target.value))}
                        >
                          {levelOptions.map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <ChevronDown size={13} className="bt-select-chevron" />
                      </div>
                      {levels.length > 1 && (
                        <button
                          className="bt-level-remove"
                          onClick={() => removeLevel(level.uid)}
                          title="Remove this level"
                        >
                          <Trash2 size={15} />
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="bt-templates-area">
                      {level.selectedTemplates.length === 0 ? (
                        <span className="bt-empty-hint">No templates selected yet</span>
                      ) : (
                        <div className="bt-tag-list">
                          {level.selectedTemplates.map(tpl => (
                            <span key={tpl.templateId} className={`bt-tag${tpl.fromDefault ? ' bt-tag-default' : ''}`}>
                              {tpl.fromDefault
                                ? <Sparkles size={13} className="bt-tag-icon" />
                                : <LayoutTemplate size={13} className="bt-tag-icon" />
                              }
                              <span className="bt-tag-name">{tpl.heading}</span>
                              <button
                                className="bt-tag-remove"
                                onClick={() => removeTemplate(level.uid, tpl.templateId)}
                                title="Remove"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="bt-picker-wrap" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>

                        {/* ── Add Default Template ── */}
                        <div style={{ position: 'relative' }}>
                          <button
                            className="bt-add-template-btn bt-add-default-btn"
                            onClick={() => toggleDefaultPicker(level.uid)}
                            disabled={creatingFromDefault === level.uid || loadingDefaults}
                            title="Pick from the shared default template library"
                          >
                            {creatingFromDefault === level.uid
                              ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Adding…</>
                              : <><Sparkles size={15} /> Add Default Template</>
                            }
                          </button>

                          {level.showDefaultPicker && (
                            <div className="bt-picker-dropdown" style={{ minWidth: '260px' }}>
                              {defaultTemplates.length === 0 ? (
                                <div className="bt-picker-item" style={{ color: '#9ca3af', cursor: 'default' }}>
                                  <span>No default templates available</span>
                                </div>
                              ) : (
                                <>
                                  {/* Group by category */}
                                  {(() => {
                                    const categories = [...new Set(defaultTemplates.map(t => t.category || 'General'))];
                                    return categories.map(cat => (
                                      <React.Fragment key={cat}>
                                        {categories.length > 1 && (
                                          <div style={{
                                            padding: '0.35rem 0.75rem 0.2rem',
                                            fontSize: '0.68rem', fontWeight: 700,
                                            color: '#667eea', textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                            borderTop: cat !== categories[0] ? '1px solid #f1f5f9' : 'none'
                                          }}>{cat}</div>
                                        )}
                                        {defaultTemplates
                                          .filter(t => (t.category || 'General') === cat)
                                          .map(tpl => (
                                            <div
                                              key={tpl.defaultTemplateId}
                                              className="bt-picker-item"
                                              onClick={() => handleAddFromDefault(level.uid, tpl)}
                                            >
                                              <Sparkles size={14} className="bt-picker-icon" style={{ color: '#667eea' }} />
                                              <div className="bt-picker-text">
                                                <span className="bt-picker-name">{tpl.heading}</span>
                                                <span className="bt-picker-meta">
                                                  {tpl.items?.length || 0} items
                                                  {tpl.category ? ` · ${tpl.category}` : ''}
                                                </span>
                                              </div>
                                            </div>
                                          ))
                                        }
                                      </React.Fragment>
                                    ));
                                  })()}
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* ── Add Template (instructor's own) ── */}
                        <div style={{ position: 'relative' }}>
                          <button
                            className="bt-add-template-btn"
                            onClick={() => togglePicker(level.uid)}
                            disabled={pickable.length === 0}
                          >
                            <PlusCircle size={15} />
                            Add Template
                          </button>

                          {level.showPicker && pickable.length > 0 && (
                            <div className="bt-picker-dropdown">
                              {pickable.map(tpl => (
                                <div
                                  key={tpl.templateId}
                                  className="bt-picker-item"
                                  onClick={() => addTemplate(level.uid, tpl)}
                                >
                                  <LayoutTemplate size={15} className="bt-picker-icon" />
                                  <div className="bt-picker-text">
                                    <span className="bt-picker-name">{tpl.heading}</span>
                                    <span className="bt-picker-meta">
                                      {tpl.items?.length || 0} items
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button className="bt-add-level-btn" onClick={addLevel}>
                <PlusCircle size={16} />
                Add Level
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Step 1 — Verify ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="tracker-step-panel">
          <h3 className="step-panel-title">Verify Configuration</h3>
          <p className="step-panel-subtitle">
            Review your saved level configuration before proceeding to Publish.
          </p>

          {verifyError && (
            <div className="error-banner" style={{ marginBottom: '1rem' }}>
              <AlertCircle size={16} /><span>{verifyError}</span>
            </div>
          )}

          {loadingVerify ? (
            <div className="loading-state"><div className="spinner" /><p>Loading…</p></div>
          ) : verifyData.length === 0 ? (
            <div className="empty-state">
              <p>No configurations found. Go back and save a configuration.</p>
            </div>
          ) : (
            <div className="verify-configs-list">
              {verifyData.map((config, ci) => (
                <div key={config.configId} className="verify-config-card">
                  <div className="verify-config-header">
                    <span className="verify-config-label">Configuration {ci + 1}</span>
                    <span className="verify-config-date">
                      {new Date(config.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="verify-levels-list">
                    {(config.levels || []).map((lv, li) => (
                      <div key={li} className="verify-level-row">
                        <span className="verify-level-badge">Level {lv.level}</span>
                        <div className="bt-tag-list" style={{ flex: 1 }}>
                          {(lv.templates || []).map(tpl => (
                            <span key={tpl.templateId} className="bt-tag">
                              <LayoutTemplate size={13} className="bt-tag-icon" />
                              <span className="bt-tag-name">{tpl.heading}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Step 2 — Publish ────────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="publish-unified-wrap">

          {publishLoadError && (
            <div className="error-banner" style={{ marginBottom: '1rem' }}>
              <AlertCircle size={16} /><span>{publishLoadError}</span>
            </div>
          )}

          {loadingPublish ? (
            <div className="loading-state"><div className="spinner" /><p>Loading…</p></div>
          ) : (
            <>
              {/* ── Info row — mirrors the 3-column table grid ── */}
              <div className="publish-info-row">
                <div className="publish-info-level-gap" />
                <div className="publish-info-cell">
                  <span className="publish-info-title">Current configuration</span>
                  <span className="publish-col-badge publish-badge-draft">Draft</span>
                  {publishConfigDate && (
                    <span className="publish-info-meta">
                      <Clock size={12} /> Saved {formatDate(publishConfigDate)}
                    </span>
                  )}
                </div>
                <div className="publish-info-cell">
                  <span className="publish-info-title">Published record</span>
                  <span className="publish-col-badge publish-badge-pub">
                    {publishedRecord ? 'Published' : 'Not published'}
                  </span>
                  {publishedRecord?.createdAt && (
                    <span className="publish-info-meta">
                      <Clock size={12} /> Published {formatDate(publishedRecord.createdAt)}
                    </span>
                  )}
                </div>
              </div>

              {/* ── Unified 3-column table ── */}
              <div className="table-container" style={{ marginBottom: 0, borderRadius: '0 0 0.75rem 0.75rem' }}>
                <table className="user-table publish-unified-table">
                  <thead>
                    <tr>
                      <th className="pub-th-level">Level</th>
                      <th className="pub-th-draft">
                        Template Heading&nbsp;
                        <span className="publish-col-badge publish-badge-draft">Draft</span>
                      </th>
                      <th className="pub-th-published">
                        Template Heading&nbsp;
                        <span className="publish-col-badge publish-badge-pub">Published</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {levelOptions.map(levelNum => {
                      const draftLv     = publishLevels.find(l => l.level === levelNum);
                      const publishedLv = (publishedRecord?.levels || []).find(l => l.level === levelNum);
                      const draftTpls   = draftLv?.templates     || [];
                      const pubTpls     = publishedLv?.templates || [];
                      const isEmpty     = draftTpls.length === 0 && pubTpls.length === 0;
                      const rowBg       = levelNum % 2 === 1 ? '#ffffff' : '#f1f5f9';

                      if (isEmpty) return null;

                      return (
                        <tr key={levelNum} className="pub-row-active">
                          {/* Level badge */}
                          <td
                            className="pub-td-level"
                            style={{ backgroundColor: rowBg }}
                          >
                            <span className="verify-level-badge">
                              Level {levelNum}
                            </span>
                          </td>

                          {/* Draft headings */}
                          <td className="pub-td-draft" style={{ backgroundColor: rowBg }}>
                            {draftTpls.length === 0 ? (
                              <span className="pub-empty-hint">—</span>
                            ) : (
                              <div className="pub-heading-stack">
                                {draftTpls.map(tpl => (
                                  <span key={tpl.templateId} className="pub-heading-item">
                                    {tpl.heading}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>

                          {/* Published headings */}
                          <td className="pub-td-published" style={{ backgroundColor: rowBg }}>
                            {pubTpls.length === 0 ? (
                              <span className="pub-empty-hint">—</span>
                            ) : (
                              <div className="pub-heading-stack">
                                {pubTpls.map(tpl => (
                                  <span key={tpl.templateId} className="pub-heading-item">
                                    {tpl.heading}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Footer: feedback + Publish button ── */}
              <div className="publish-unified-footer">
                <div className="publish-unified-footer-left">
                  {publishError && (
                    <div className="error-banner">
                      <AlertCircle size={16} /><span>{publishError}</span>
                    </div>
                  )}
                  {publishSuccess && (
                    <div className="success-banner">
                      <CheckCircle2 size={16} />
                      <span>
                        {publishedRecord ? 'TrackerLevels record updated.' : 'Configuration published to TrackerLevels.'}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  className="btn-new"
                  onClick={handlePublish}
                  disabled={isPublishing || publishLevels.length === 0 || !hasDiff}
                  style={{ opacity: isPublishing || publishLevels.length === 0 || !hasDiff ? 0.5 : 1, flexShrink: 0 }}
                >
                  {isPublishing ? 'Publishing…' : (
                    <><Send size={15} style={{ marginRight: 6 }} />
                      {publishedRecord ? 'Update Published Record' : 'Publish'}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="tracker-nav-buttons" style={{ marginTop: '1.25rem' }}>
        {step > 0 && (
          <button className="btn-secondary" onClick={() => setStep(s => s - 1)}>
            Back
          </button>
        )}

        {step === 0 && (
          <button
            className="btn-new"
            onClick={handleSaveAndContinue}
            disabled={isSaving || !isValid}
            style={{ marginLeft: 'auto', opacity: isSaving || !isValid ? 0.5 : 1 }}
          >
            {isSaving ? 'Saving…' : (
              <>Save &amp; Continue <ChevronRight size={16} style={{ marginLeft: 4 }} /></>
            )}
          </button>
        )}

        {step === 1 && (
          <button
            className="btn-new"
            onClick={() => goForward(2)}
            style={{ marginLeft: 'auto' }}
          >
            Save &amp; Continue <ChevronRight size={16} style={{ marginLeft: 4 }} />
          </button>
        )}
      </div>
    </div>
  );
};

export default BuildTracker;